import {
  Button,
  Box,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Textarea,
  Text,
  VStack,
  useColorModeValue as mode,
  useDisclosure,
  useToast,
} from "@chakra-ui/react"
import { ChevronLeftIcon, ChevronRightIcon, WarningIcon } from "@chakra-ui/icons"
import { useCallback, useEffect, useRef, useState } from "react"
import axios from "axios"
import { useSelector } from "react-redux"
import { getApiBaseUrl } from "../utils/axiosConfig"
import JobCard from "./JobCard"

const CallerIdLiveListener = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()
  const { userInfo } = useSelector((state) => state.user)
  const [incomingCall, setIncomingCall] = useState(null)
  const [callHistory, setCallHistory] = useState([])
  const [callHistoryIndex, setCallHistoryIndex] = useState(0)
  const [callHistoryLoading, setCallHistoryLoading] = useState(false)
  const [customerJobs, setCustomerJobs] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [isAutoClosePaused, setIsAutoClosePaused] = useState(false)
  const [spamLoading, setSpamLoading] = useState(false)
  const [noteLoading, setNoteLoading] = useState(false)
  const [isNoteEditorOpen, setIsNoteEditorOpen] = useState(false)
  const [noteDraft, setNoteDraft] = useState("")
  const closeTimerRef = useRef(null)
  const eventSourceRef = useRef(null)
  const reconnectTimerRef = useRef(null)
  const staleCheckTimerRef = useRef(null)
  const lastEventAtRef = useRef(0)
  const connectionOkRef = useRef(false)
  const autoCloseMs = Number(process.env.REACT_APP_CALLER_MODAL_AUTOCLOSE_MS || 20000)

  const mapCallerPayloadToDialogState = (payload) => {
    const customerLabel = payload?.customerName
      ? payload.customerName
      : "Number not in database"

    return {
      callerName: payload?.callerName || "Unknown",
      callerNumber: payload?.callerNumber || "Unknown",
      customerLabel,
      customerFound: Boolean(payload?.customerFound),
      customerId: payload?.customerId || null,
      lastCallMessage: payload?.lastCallMessage || null,
      lastCallReceivedAt: payload?.lastCallReceivedAt || null,
      isSpam: Boolean(payload?.isSpam),
      callerNote: payload?.callerNote || null,
      timestamp: payload?.timestamp || null,
    }
  }

  const getActiveCallerNumberDigits = useCallback(() => {
    return String(incomingCall?.callerNumber || "").replace(/\D/g, "")
  }, [incomingCall?.callerNumber])

  const applyNumberMeta = useCallback(
    (meta) => {
      const nextIsSpam = Boolean(meta?.isSpam)
      const nextNote = meta?.callerNote || null

      setIncomingCall((prev) => {
        if (!prev) {
          return prev
        }

        return {
          ...prev,
          isSpam: nextIsSpam,
          callerNote: nextNote,
        }
      })

      setCallHistory((prev) => {
        if (!prev.length || callHistoryIndex < 0 || callHistoryIndex >= prev.length) {
          return prev
        }

        return prev.map((entry, index) => {
          if (index !== callHistoryIndex) {
            return entry
          }

          return {
            ...entry,
            isSpam: nextIsSpam,
            callerNote: nextNote,
          }
        })
      })
    },
    [callHistoryIndex]
  )

  const handleToggleSpam = useCallback(async () => {
    const callerNumber = getActiveCallerNumberDigits()
    if (!callerNumber) {
      return
    }

    setSpamLoading(true)
    try {
      const { data } = await axios.post("/api/caller-id/number-meta/toggle-spam", {
        callerNumber,
      })

      applyNumberMeta(data)
    } catch (error) {
      console.error("Failed to toggle spam status:", error)
    } finally {
      setSpamLoading(false)
    }
  }, [applyNumberMeta, getActiveCallerNumberDigits])

  const handleOpenNoteEditor = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    setIsAutoClosePaused(true)
    setNoteDraft(incomingCall?.callerNote || "")
    setIsNoteEditorOpen(true)
  }, [incomingCall?.callerNote])

  const handleCloseNoteEditor = useCallback(() => {
    setIsNoteEditorOpen(false)
  }, [])

  const handleSaveNote = useCallback(async () => {
    const callerNumber = getActiveCallerNumberDigits()
    if (!callerNumber) {
      return
    }

    setNoteLoading(true)
    try {
      const { data } = await axios.put("/api/caller-id/number-meta/note", {
        callerNumber,
        note: noteDraft,
      })

      applyNumberMeta(data)
      setIsNoteEditorOpen(false)
    } catch (error) {
      console.error("Failed to save caller note:", error)
    } finally {
      setNoteLoading(false)
    }
  }, [applyNumberMeta, getActiveCallerNumberDigits, noteDraft])

  const emitConnectionStatus = (connected, reason) => {
    connectionOkRef.current = connected

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("caller-sse-status", {
          detail: {
            connected,
            checkedAt: new Date().toISOString(),
            reason,
          },
        })
      )
    }
  }

  const applyCallHistorySelection = useCallback((historyEntry) => {
    if (!historyEntry) {
      return
    }

    setIncomingCall(mapCallerPayloadToDialogState(historyEntry))
  }, [])

  const loadGlobalCallerHistory = useCallback(async () => {
    setCallHistoryLoading(true)
    try {
      const { data } = await axios.get("/api/caller-id/history?limit=30")
      const history = data?.history || []
      setCallHistory(history)

      if (history.length > 0) {
        setCallHistoryIndex(0)
        applyCallHistorySelection(history[0])
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error("Failed to load caller history:", error)
      }
      setCallHistory([])
      setCallHistoryIndex(0)
    } finally {
      setCallHistoryLoading(false)
    }
  }, [applyCallHistorySelection])

  const playIncomingCallSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (!AudioCtx) {
        return
      }

      const context = new AudioCtx()
      const playTone = (startAt, duration, frequency) => {
        const oscillator = context.createOscillator()
        const gainNode = context.createGain()

        oscillator.type = "sine"
        oscillator.frequency.setValueAtTime(frequency, startAt)

        gainNode.gain.setValueAtTime(0.001, startAt)
        gainNode.gain.exponentialRampToValueAtTime(0.15, startAt + 0.02)
        gainNode.gain.exponentialRampToValueAtTime(0.001, startAt + duration)

        oscillator.connect(gainNode)
        gainNode.connect(context.destination)

        oscillator.start(startAt)
        oscillator.stop(startAt + duration)
      }

      const now = context.currentTime
      playTone(now, 0.2, 880)
      playTone(now + 0.3, 0.2, 988)

      setTimeout(() => {
        context.close().catch(() => {})
      }, 800)
    } catch (error) {
      // Audio can be blocked by browser policy; modal still opens.
      console.error("Failed to play incoming call sound:", error)
    }
  }

  const handleModalClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setIsAutoClosePaused(false)
    setIsNoteEditorOpen(false)
    onClose()
  }

  const pauseAutoCloseOnInteraction = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    if (!isAutoClosePaused) {
      setIsAutoClosePaused(true)
    }
  }, [isAutoClosePaused])

  useEffect(() => {
    if (!userInfo?.token) {
      return
    }

    const reconnectDelayMs = Number(process.env.REACT_APP_CALLER_SSE_RECONNECT_MS || 3000)
    const staleAfterMs = Number(process.env.REACT_APP_CALLER_SSE_STALE_MS || 90000)
    const staleCheckIntervalMs = Number(process.env.REACT_APP_CALLER_SSE_CHECK_MS || 15000)
    const apiBaseUrl = getApiBaseUrl()
    const streamPath = "/api/caller-id/stream"
    const streamUrl = `${apiBaseUrl || ""}${streamPath}?token=${encodeURIComponent(userInfo.token)}`
    let disposed = false

    const markEventActivity = () => {
      lastEventAtRef.current = Date.now()
    }

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
    }

    const closeEventSource = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }

    const scheduleReconnect = () => {
      if (disposed || reconnectTimerRef.current) {
        return
      }

      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null
        connectStream()
      }, reconnectDelayMs)
    }

    const connectStream = () => {
      if (disposed) {
        return
      }

      closeEventSource()
      markEventActivity()

      const eventSource = new EventSource(streamUrl)
      eventSourceRef.current = eventSource

      eventSource.onopen = () => {
        markEventActivity()
        clearReconnectTimer()
        emitConnectionStatus(true, "open")
      }

      eventSource.onerror = () => {
        emitConnectionStatus(false, "error")
        scheduleReconnect()
      }

      eventSource.addEventListener("connected", () => {
        markEventActivity()
        emitConnectionStatus(true, "connected-event")
      })

      eventSource.addEventListener("ping", () => {
        markEventActivity()
        emitConnectionStatus(true, "ping")
      })

      eventSource.addEventListener("caller-id", (event) => {
        markEventActivity()
        emitConnectionStatus(true, "caller-id")
        try {
          const payload = JSON.parse(event.data)
          setIncomingCall(mapCallerPayloadToDialogState(payload))
          setIsAutoClosePaused(false)
          loadGlobalCallerHistory()
          playIncomingCallSound()
          onOpen()
        } catch (error) {
          console.error("Failed to parse caller ID stream payload:", error)
        }
      })
    }

    const ensureStreamFreshness = () => {
      const timeSinceLastEvent = Date.now() - lastEventAtRef.current
      const streamState = eventSourceRef.current?.readyState
      const hasStaleConnection = timeSinceLastEvent > staleAfterMs
      const isClosed = streamState === EventSource.CLOSED
      const isOpen = streamState === EventSource.OPEN

      if (hasStaleConnection || isClosed) {
        emitConnectionStatus(false, hasStaleConnection ? "stale" : "closed")
        scheduleReconnect()
        return
      }

      emitConnectionStatus(Boolean(isOpen), "health-check")
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        ensureStreamFreshness()
      }
    }

    connectStream()

    staleCheckTimerRef.current = setInterval(() => {
      ensureStreamFreshness()
    }, staleCheckIntervalMs)

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      disposed = true
      emitConnectionStatus(false, "cleanup")
      clearReconnectTimer()
      if (staleCheckTimerRef.current) {
        clearInterval(staleCheckTimerRef.current)
        staleCheckTimerRef.current = null
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      closeEventSource()
    }
  }, [loadGlobalCallerHistory, onOpen, userInfo?.token])

  useEffect(() => {
    const handleOpenLastCallerDialog = () => {
      if (isOpen) {
        return
      }

      const fetchLastCaller = async () => {
        try {
          await loadGlobalCallerHistory()
          setIsAutoClosePaused(false)
          onOpen()
        } catch (error) {
          if (error?.response?.status !== 404) {
            console.error("Failed to fetch last caller info:", error)
          }
        }
      }

      fetchLastCaller()
    }

    window.addEventListener("caller-dialog-open-last", handleOpenLastCallerDialog)

    return () => {
      window.removeEventListener("caller-dialog-open-last", handleOpenLastCallerDialog)
    }
  }, [isOpen, loadGlobalCallerHistory, onOpen])

  useEffect(() => {
    if (callHistory.length === 0) {
      return
    }

    const selectedHistoryEntry = callHistory[callHistoryIndex]
    applyCallHistorySelection(selectedHistoryEntry)
  }, [applyCallHistorySelection, callHistory, callHistoryIndex])

  useEffect(() => {
    if (!isOpen || !autoCloseMs || autoCloseMs <= 0 || isAutoClosePaused) {
      return
    }

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
    }

    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null
      onClose()
    }, autoCloseMs)

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    }
  }, [autoCloseMs, isAutoClosePaused, isOpen, incomingCall, onClose])

  useEffect(() => {
    const fetchCustomerJobs = async () => {
      if (!isOpen || !incomingCall?.customerFound || !incomingCall?.customerId) {
        setCustomerJobs([])
        setHistoryLoading(false)
        return
      }

      setHistoryLoading(true)
      try {
        const { data } = await axios.get(`/api/jobs/customer/${incomingCall.customerId}`)
        setCustomerJobs(data.jobs || [])
      } catch (error) {
        console.error("Failed to load customer history for incoming call:", error)
        setCustomerJobs([])
      } finally {
        setHistoryLoading(false)
      }
    }

    fetchCustomerJobs()
  }, [incomingCall?.customerFound, incomingCall?.customerId, isOpen])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  const historyBg = mode("gray.50", "gray.700")
  const historyTextColor = mode("gray.600", "gray.300")

  const formatCallerNumber = (value) => {
    const raw = String(value || "")
    const digitsOnly = raw.replace(/\D/g, "")

    if (digitsOnly.length !== 10) {
      return raw || "Unknown"
    }

    if (digitsOnly.startsWith("04")) {
      return `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7, 10)}`
    }

    return `${digitsOnly.slice(0, 2)}-${digitsOnly.slice(2, 6)}-${digitsOnly.slice(6, 10)}`
  }

  const formatRelativeTimestamp = (dateValue) => {
    if (!dateValue) {
      return null
    }

    const inputDate = new Date(dateValue)
    if (Number.isNaN(inputDate.getTime())) {
      return null
    }

    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const yesterdayStart = new Date(todayStart)
    yesterdayStart.setDate(yesterdayStart.getDate() - 1)
    const inputDayStart = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate())
    const timePart = inputDate.toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
    })

    if (inputDayStart.getTime() === todayStart.getTime()) {
      return `Today at ${timePart}`
    }

    if (inputDayStart.getTime() === yesterdayStart.getTime()) {
      return `Yesterday at ${timePart}`
    }

    const datePart = inputDate.toLocaleDateString("en-AU")
    return `On ${datePart} at ${timePart}`
  }

  const formatLastCallLabel = () => {
    if (!incomingCall?.lastCallReceivedAt) {
      return "No previous calls recorded"
    }

    const formatted = formatRelativeTimestamp(incomingCall.lastCallReceivedAt)
    return formatted ? `Last call received: ${formatted}` : (incomingCall?.lastCallMessage || "No previous calls recorded")
  }

  const copyTextToClipboard = async (textValue) => {
    if (!textValue) {
      return false
    }

    const normalizedValue = String(textValue)

    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(normalizedValue)
        return true
      } catch (error) {
        console.warn("Clipboard API failed, falling back to execCommand", error)
      }
    }

    try {
      const tempInput = document.createElement("textarea")
      tempInput.value = normalizedValue
      tempInput.setAttribute("readonly", "")
      tempInput.setAttribute("aria-hidden", "true")
      tempInput.style.position = "fixed"
      tempInput.style.top = "-9999px"
      tempInput.style.left = "-9999px"
      tempInput.style.opacity = "0"
      tempInput.style.width = "1px"
      tempInput.style.height = "1px"
      document.body.appendChild(tempInput)

      return await new Promise((resolve) => {
        window.setTimeout(() => {
          try {
            tempInput.focus()
            tempInput.select()
            tempInput.setSelectionRange(0, tempInput.value.length)
            const copied = document.execCommand("copy")
            document.body.removeChild(tempInput)
            resolve(copied)
          } catch (error) {
            document.body.removeChild(tempInput)
            console.error("Failed to copy caller number:", error)
            resolve(false)
          }
        }, 0)
      })
    } catch (error) {
      console.error("Failed to copy caller number:", error)
      return false
    }
  }

  const handleCopyCallerNumber = async () => {
    pauseAutoCloseOnInteraction()
    const rawDigits = String(incomingCall?.callerNumber || "").replace(/\D/g, "")
    const copied = await copyTextToClipboard(rawDigits)

    if (copied) {
      toast({
        description: "Number copied to clipboard",
        status: "success",
        duration: 1500,
        isClosable: true,
        position: "top",
      })
      return
    }

    toast({
      description: "Could not copy number",
      status: "warning",
      duration: 1800,
      isClosable: true,
      position: "top",
    })
  }

  return (
    <>
    <Modal isOpen={isOpen} onClose={handleModalClose} isCentered size='2xl'>
      <ModalOverlay />
      <ModalContent
        maxH='90vh'
        tabIndex={0}
        onMouseDown={pauseAutoCloseOnInteraction}
        onWheel={pauseAutoCloseOnInteraction}
        onTouchStart={pauseAutoCloseOnInteraction}
        onKeyDown={pauseAutoCloseOnInteraction}
        onScrollCapture={pauseAutoCloseOnInteraction}
      >
        <ModalHeader display='flex' justifyContent='center' alignItems='center' gap='4' fontSize='3xl' fontWeight='black'>
          <Text as='span'>Incoming Call</Text>
          {incomingCall?.timestamp && (
            <Text as='span' fontSize='sm' fontWeight='semibold' color={historyTextColor} mt={1}> 
              ({formatRelativeTimestamp(incomingCall.timestamp) || "Unknown time"})
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing='2' align='stretch'>
            <Box borderWidth='1px' borderRadius='md' p='2'>
              <VStack spacing='2' align='stretch'>
                <HStack justifyContent='space-between' alignItems='start' spacing='3'>
                  <VStack align='start' spacing='0'>
                    <Text fontSize='xs' fontWeight='bold' letterSpacing='0.12em' textTransform='uppercase' color={historyTextColor}>
                      Caller Details
                    </Text>
                  </VStack>
                  <HStack spacing='2' flexShrink={0} alignItems='center'>
                    <Button
                      size='sm'
                      colorScheme={incomingCall?.isSpam ? "red" : "gray"}
                      variant={incomingCall?.isSpam ? "solid" : "outline"}
                      onClick={handleToggleSpam}
                      isLoading={spamLoading}
                      loadingText='Saving'
                    >
                      Spam
                    </Button>
                    <Button
                      size='sm'
                      colorScheme='cyan'
                      variant='outline'
                      onClick={handleOpenNoteEditor}
                      isLoading={noteLoading}
                      loadingText='Opening'
                    >
                      Note
                    </Button>
                  </HStack>
                </HStack>
                <HStack justifyContent='center' alignItems='center' spacing='2'>
                  {incomingCall?.isSpam && <WarningIcon color='red.500' boxSize={5} title='Spam number' />}
                  <Text fontSize='xl' textAlign='center'>
                    Number:{" "}
                    <Text
                      as='span'
                      fontWeight='bold'
                      cursor='pointer'
                      color='#1A365D'
                      _hover={{ color: "#153E75" }}
                      title='Click to copy raw number'
                      onClick={handleCopyCallerNumber}
                    >
                      {formatCallerNumber(incomingCall?.callerNumber)}
                    </Text>
                  </Text>
                  {incomingCall?.isSpam && <WarningIcon color='red.500' boxSize={5} title='Spam number' />}
                </HStack>
                <Text fontSize='lg' textAlign='center' lineHeight='short'>
                  Customer: <Text as='span' fontWeight='bold'>{incomingCall?.customerLabel || "Number not in database"}</Text>
                </Text>
                {incomingCall?.callerNote && (
                  <Text
                    fontSize='sm'
                    textAlign='center'
                    color={historyTextColor}
                    lineHeight='short'
                    p='2'
                    borderRadius='md'
                    bg={mode("blue.50", "blue.900")}
                    border='1px solid'
                    borderColor={mode("blue.100", "blue.700")}
                  >
                    Note: {incomingCall.callerNote}
                  </Text>
                )}
                <Text fontSize='md' textAlign='center' color={historyTextColor} lineHeight='short'>
                  {formatLastCallLabel()}
                </Text>
                <HStack justifyContent='space-between' alignItems='center' pt='0'>
                  <IconButton
                    aria-label='Older call history event'
                    icon={<ChevronLeftIcon boxSize={5} />}
                    size='sm'
                    colorScheme='cyan'
                    variant='outline'
                    onClick={() => setCallHistoryIndex((prev) => Math.min(prev + 1, callHistory.length - 1))}
                    isDisabled={callHistoryLoading || callHistory.length <= 1 || callHistoryIndex >= callHistory.length - 1}
                  />
                  <Text fontSize='sm' color={historyTextColor} fontWeight='semibold'>
                    {callHistoryLoading
                      ? "Loading call history..."
                      : callHistory.length > 0
                      ? `${callHistoryIndex + 1} / ${callHistory.length}`
                      : "No call history events"}
                  </Text>
                  <IconButton
                    aria-label='Newer call history event'
                    icon={<ChevronRightIcon boxSize={5} />}
                    size='sm'
                    colorScheme='cyan'
                    variant='outline'
                    onClick={() => setCallHistoryIndex((prev) => Math.max(prev - 1, 0))}
                    isDisabled={callHistoryLoading || callHistory.length <= 1 || callHistoryIndex <= 0}
                  />
                </HStack>
              </VStack>
            </Box>

            <Box borderWidth='1px' borderRadius='md' p='3'>
              <Text fontSize='lg' fontWeight='bold' mb='3'>
                Job History
              </Text>
              <Box maxH='45vh' overflowY='auto' pr='1'>
                {!incomingCall?.customerFound ? (
                  <Box bg={historyBg} p='4' borderRadius='md' textAlign='center'>
                    <Text color={historyTextColor}>Number not in database</Text>
                  </Box>
                ) : historyLoading ? (
                  <Box display='flex' justifyContent='center' alignItems='center' minH='180px'>
                    <Spinner size='lg' />
                  </Box>
                ) : customerJobs.length === 0 ? (
                  <Box bg={historyBg} p='4' borderRadius='md' textAlign='center'>
                    <Text color={historyTextColor}>No jobs found for this customer</Text>
                  </Box>
                ) : (
                  <VStack spacing='4' align='stretch'>
                    {customerJobs.map((job) => (
                      <JobCard
                        key={job.JOB_NO}
                        job={job}
                        loading={false}
                        listType='history'
                        onJobCardClick={handleModalClose}
                      />
                    ))}
                  </VStack>
                )}
              </Box>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter justifyContent='center'>
          <Button colorScheme='cyan' size='lg' onClick={handleModalClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    <Modal isOpen={isNoteEditorOpen} onClose={handleCloseNoteEditor} isCentered size='lg'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Caller Note</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize='sm' mb='2' color={historyTextColor}>
            Add a note for {formatCallerNumber(incomingCall?.callerNumber)}
          </Text>
          <Textarea
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            placeholder='Type note or comment...'
            minH='130px'
            maxLength={300}
            resize='vertical'
          />
          <Text textAlign='right' fontSize='xs' color={historyTextColor} mt='1'>
            {noteDraft.length}/300
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button variant='ghost' mr='3' onClick={handleCloseNoteEditor} isDisabled={noteLoading}>
            Cancel
          </Button>
          <Button colorScheme='cyan' onClick={handleSaveNote} isLoading={noteLoading} loadingText='Saving'>
            Save Note
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  )
}

export default CallerIdLiveListener
